import React, { useState } from 'react';
import { Modal, Form, Input, Button, Upload, message, Spin, Skeleton } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAddCategoryMutation } from '@/services/api/category/categoryApi';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';
import useAuth from '@/hooks/useAuth';

const { TextArea } = Input;

const AddCategoryModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState('');
  const imageUploadRef = React.useRef(null);
  const authUser = useAuth();
  const [addCategory, { isLoading }] = useAddCategoryMutation();

  // Submit form
  const handleSubmit = async (values) => {
    // Validate image upload - check for both null/undefined and empty string
    if (!imageFile && (!imageUrl || imageUrl.trim() === '')) {
      setImageError('Please upload a category image');
      message.error('Please upload a category image');
      // Scroll to image upload field
      if (imageUploadRef.current) {
        imageUploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      let uploadedImageUrl = imageUrl;

      if (imageFile instanceof File) {
        message.loading("Uploading image...", 0);
        uploadedImageUrl = await uploadToCloudinary(imageFile);
        message.destroy();
        message.success("Image uploaded successfully!");
      }

      // Double-check before sending to API
      if (!uploadedImageUrl || uploadedImageUrl.trim() === '') {
        message.error('Please upload a category image');
        setImageError('Please upload a category image');
        if (imageUploadRef.current) {
          imageUploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setIsSubmitting(false);
        return;
      }

      const payload = {
        name: values.name,
        description: values.description || '',
        url: uploadedImageUrl,
        created_by: authUser?.id,
      };

      await addCategory(payload).unwrap();
      message.success('Category created successfully!');

      form.resetFields();
      setImageUrl('');
      setImageFile(null);
      setImageError('');
      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      console.error('Error creating category:', error);
      // Filter out backend URL validation errors and show our custom message
      const errorMessage = error?.data?.message || error.message || 'Failed to create category';
      if (errorMessage.toLowerCase().includes('url') || errorMessage.toLowerCase().includes('string')) {
        message.error('Please upload a category image');
        setImageError('Please upload a category image');
        if (imageUploadRef.current) {
          imageUploadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle image upload and preview
  const handleImageUpload = (info) => {
    const file = info.file.originFileObj || info.file;
    if (!file) return;

    // Validate file type & size
    if (!file.type.startsWith('image/')) {
      message.error('You can only upload image files!');
      return;
    }
    if (file.size / 1024 / 1024 >= 2) {
      message.error('Image must be smaller than 2MB!');
      return;
    }

    // Show preview
    const previewUrl = URL.createObjectURL(file);
    setImageUrl(previewUrl);
    setImageFile(file);
    setImageError(''); // Clear error when image is uploaded
  };

  // Remove image
  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImageUrl('');
    setImageFile(null);
    setImageError('Please upload a category image');
  };

  // Handle cancel
  const handleCancel = () => {
    form.resetFields();
    setImageUrl('');
    setImageFile(null);
    setImageError('');
    onCancel();
  };

  return (
    <Modal
      title="Add New Category"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={isSubmitting || isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-4"
        >
          {/* Category Name */}
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 100, message: 'Name must not exceed 100 characters' },
            ]}
          >
            <Input placeholder="Enter category name" size="large" />
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: 'Description must not exceed 500 characters' }]}
          >
            <TextArea
              placeholder="Enter category description (optional)"
              rows={4}
              showCount
              maxLength={500}
            />
          </Form.Item>

          {/* Category Icon Upload - Now Required */}
          <Form.Item
            ref={imageUploadRef}
            label={
              <span>
                Category Icon <span className="text-red-500">*</span>
              </span>
            }
            validateStatus={imageError ? 'error' : ''}
            help={imageError || 'Click to upload or drag and drop. Max. File Size: 2MB'}
          >
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              imageError ? 'border-red-300 hover:border-red-400' : 'border-blue-300 hover:border-blue-400'
            }`}>
              <Upload
                name="image"
                listType="picture-card"
                className="category-uploader w-full"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageUpload}
              >
                {imageUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={imageUrl}
                      alt="category preview"
                      className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                    />
                    <Button
                      type="text"
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-white shadow-md hover:bg-red-50"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <PlusOutlined className="text-2xl mb-2" />
                    <div>Click or drag to upload</div>
                    <small className="text-gray-400">Max size: 2MB</small>
                  </div>
                )}
              </Upload>
            </div>
          </Form.Item>

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 mt-8">
            <Button onClick={handleCancel} size="large" className="px-6">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitting || isLoading}
              className="bg-orange-500 border-orange-500 hover:bg-orange-600 px-6 rounded-lg"
            >
              {isSubmitting || isLoading ? 'Creating...' : 'Save'}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddCategoryModal;